import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Brain, Loader2, Save } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkerStatus } from "@/hooks/use-worker-status";
import { WorkerStatusChip } from "@/components/claude-worker-ui";
import type { AiEngine } from "@/lib/ai-engine";

/** apiRequest throws `Error("<status>: <body>")`; pull the server's `message` out of a JSON body when present. */
function serverMessage(err: unknown): string | undefined {
  const raw = err instanceof Error ? err.message : "";
  const body = raw.replace(/^\d+:\s*/, "");
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed.message === "string") return parsed.message;
  } catch { /* not JSON */ }
  return body || undefined;
}

const ENGINE_OPTIONS: { value: AiEngine; label: string; desc: string }[] = [
  {
    value: "hybrid",
    label: "Hybrid (recommended): Gemini chats instantly, the Claude team does the work",
    desc: "Draper replies right away. Articles, posts, plans and other production work are handed to the Claude team; finished drafts appear under Needs your review in Content Studio, and nothing goes out until you approve it.",
  },
  {
    value: "claude_worker",
    label: "Claude worker (everything via Claude, slower chat)",
    desc: "Every chat message is queued for the Claude worker, which replies in about 1–5 minutes. Use this if you want Claude behind every answer.",
  },
  {
    value: "gemini",
    label: "Gemini (legacy)",
    desc: "The original built-in setup: Draper and the agents run on Gemini and everything is instant. Keeps the original Content Studio tools.",
  },
];

const SAVED_COPY: Record<AiEngine, string> = {
  hybrid: "Draper chats instantly and hands production work to the Claude team. Finished drafts show up under Needs your review.",
  claude_worker: "Draper chat now queues every message for the Claude worker.",
  gemini: "Draper chat and the agents now use built-in Gemini.",
};

/**
 * Workspace setting: which brain answers Draper's chat. Saves `{ aiEngine }` through the
 * existing workspace PATCH route and invalidates the workspace query so every chat surface
 * picks up the change.
 */
export function AiEngineSetting({ workspaceId, currentEngine, disabled, disabledReason }: {
  workspaceId: number;
  currentEngine: AiEngine;
  disabled?: boolean;
  /** Shown as a tooltip and note when `disabled` (e.g. the caller lacks the role to change the engine). */
  disabledReason?: string;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<AiEngine>(currentEngine);
  useEffect(() => { setSelected(currentEngine); }, [currentEngine, workspaceId]);

  const workerStatus = useWorkerStatus(workspaceId, currentEngine);

  const save = useMutation({
    mutationFn: async (aiEngine: AiEngine) => {
      const res = await apiRequest("PATCH", `/api/workspaces/${workspaceId}`, { aiEngine });
      return res.json();
    },
    onSuccess: (_data, aiEngine) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workers/status"] });
      toast({
        title: "AI engine saved",
        description: SAVED_COPY[aiEngine],
      });
    },
    onError: (err: unknown) => {
      setSelected(currentEngine);
      toast({ title: "Failed to save AI engine", description: serverMessage(err), variant: "destructive" });
    },
  });

  const dirty = selected !== currentEngine;

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-ai-engine">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <Brain size={20} className="text-blue-400" />
        <h2 className="font-display font-bold text-foreground">AI engine</h2>
        <WorkerStatusChip status={workerStatus} className="ml-auto" />
      </div>
      <p className="text-xs text-muted-foreground/70" data-testid="text-ai-engine-description">
        Choose how Draper works. Hybrid keeps chat instant and lets the Claude team do the production work. Claude worker sends every message to Claude (slower chat). Gemini is the original built-in setup.
      </p>

      <div role="radiogroup" aria-label="AI engine" className="space-y-3">
        {ENGINE_OPTIONS.map(({ value, label, desc }) => {
          const checked = selected === value;
          return (
            <label
              key={value}
              className={`flex items-start gap-3 w-full text-left px-4 py-3 rounded-xl border transition-all ${
                disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              } ${checked ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-muted/20 hover:border-border/70"}`}
              data-testid={`option-ai-engine-${value}`}
            >
              <input
                type="radio"
                name="ai-engine"
                value={value}
                checked={checked}
                disabled={disabled || save.isPending}
                onChange={() => setSelected(value)}
                className="mt-1 accent-blue-500"
                data-testid={`radio-ai-engine-${value}`}
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground">{label}</span>
                <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{desc}</span>
              </span>
            </label>
          );
        })}
      </div>

      <button
        data-testid="button-save-ai-engine"
        onClick={() => save.mutate(selected)}
        disabled={!dirty || save.isPending || disabled}
        title={disabled ? (disabledReason ?? "You don't have permission to change this.") : undefined}
        className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
      >
        {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save AI Engine
      </button>
      {disabled && disabledReason && (
        <p className="text-[11px] text-muted-foreground/60" data-testid="text-ai-engine-disabled-reason">{disabledReason}</p>
      )}
    </div>
  );
}
